// Renders form fields from a (subset of) JSON schema: string / boolean / integer / number / enum,
// `format: password` → password input, `format: uri` → url input.
import type { JsonSchemaObject, JsonSchemaProp } from "../api/types";

export type FormValues = Record<string, unknown>;

export function schemaDefaults(schema: JsonSchemaObject | null | undefined): FormValues {
  const out: FormValues = {};
  for (const [k, p] of Object.entries(schema?.properties ?? {})) {
    if (p.default !== undefined) out[k] = p.default;
    else if (p.type === "boolean") out[k] = false;
  }
  return out;
}

function humanize(key: string): string {
  const s = key.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function inputType(p: JsonSchemaProp, key: string): string {
  if (p.format === "password" || /password|secret|token/i.test(key)) return "password";
  if (p.format === "uri" || p.format === "url") return "url";
  if (p.format === "email") return "email";
  if (p.type === "integer" || p.type === "number") return "number";
  return "text";
}

export function SchemaFields({
  schema,
  values,
  onChange,
  idPrefix,
}: {
  schema: JsonSchemaObject | null | undefined;
  values: FormValues;
  onChange: (values: FormValues) => void;
  idPrefix: string;
}) {
  const props = Object.entries(schema?.properties ?? {});
  const required = new Set(schema?.required ?? []);
  return (
    <>
      {props.map(([key, p]) => {
        const id = `${idPrefix}-${key}`;
        const label = p.title ?? humanize(key);
        const set = (v: unknown) => onChange({ ...values, [key]: v });
        if (p.type === "boolean") {
          return (
            <label key={key} className="field checkbox" htmlFor={id}>
              <input id={id} type="checkbox" checked={!!values[key]} onChange={(e) => set(e.target.checked)} />
              <span>{label}</span>
              {p.description && <small className="muted">{p.description}</small>}
            </label>
          );
        }
        if (p.enum) {
          return (
            <label key={key} className="field" htmlFor={id}>
              <span>{label}</span>
              <select
                id={id}
                required={required.has(key)}
                value={String(values[key] ?? "")}
                onChange={(e) => {
                  const raw = e.target.value;
                  const match = p.enum!.find((x) => String(x) === raw);
                  set(match ?? raw);
                }}
              >
                {!required.has(key) && <option value="">—</option>}
                {p.enum.map((v) => (
                  <option key={String(v)} value={String(v)}>
                    {String(v)}
                  </option>
                ))}
              </select>
              {p.description && <small className="muted">{p.description}</small>}
            </label>
          );
        }
        const type = inputType(p, key);
        return (
          <label key={key} className="field" htmlFor={id}>
            <span>{label}</span>
            <input
              id={id}
              type={type}
              required={required.has(key)}
              autoComplete={type === "password" ? "new-password" : "off"}
              value={values[key] === undefined || values[key] === null ? "" : String(values[key])}
              onChange={(e) => {
                const v = e.target.value;
                set(type === "number" ? (v === "" ? undefined : Number(v)) : v);
              }}
            />
            {p.description && <small className="muted">{p.description}</small>}
          </label>
        );
      })}
    </>
  );
}

/** Drop empty strings/undefined so optional fields are omitted. */
export function cleanValues(values: FormValues): FormValues {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => v !== "" && v !== undefined));
}
